import '@ghq-abi/design-system/css';
import '@ghq-abi/design-system-v2/css';

import { ReactNode, useState } from 'react';
import { GetServerSideProps } from 'next';
import NextNProgress from 'nextjs-progressbar';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import { DesignSystemProvider, theme } from '@ghq-abi/design-system';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ErrorBoundary } from '~/app/components';
import { AbilityProvider } from '~/app/contexts/AbilityContext';
import { FlagsProvider } from '~/app/contexts/FlagsContext';
import MixPanelProvider from '~/app/contexts/MixPanelContext';
import { NavProvider } from '~/app/contexts/NavContext';
import { ToastProvider } from '~/app/contexts/ToastContext';
import { globalStyles } from '~/app/styles/global';
import { AppTemplate } from '~/app/templates/App';
import { CustomAppProps } from '~/app/types';
import { createUserAbility } from '~/shared/auth/permissions';
import { NPS } from '~/shared/components/Metrics';
import { useExchangeRatesBootstrap } from '~/shared/hooks/useCurrencyConversion';
import { queryClientConfig, useDatadog } from '~/shared/lib';
import { getEnv } from '~/shared/utils/environments';

import { TolgeeContextProvider } from '../app/contexts/TolgeeContext';

import { getServerSessionWrapper } from './api/auth/[...nextauth]';

import '../app/globals.css';

globalStyles();

const appEnv = getEnv();

export const getServerSideProps: GetServerSideProps = async ({ req, res }) => {
    const session = await getServerSessionWrapper(req, res);

    if (session?.user?.oneVerseRoles === null) {
        return { redirect: { destination: '/auth/error', permanent: false } };
    }

    return { props: { session } };
};

export default function App({
    Component,
    pageProps: { session, messages, abilityRules, isMobile, flags, ...pageProps },
}: CustomAppProps) {
    // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
    const [queryClient] = useState(() => new QueryClient(queryClientConfig));

    const locale = session?.user?.preferredLanguage || 'en-US';
    useDatadog(locale, session);

    return (
        <TolgeeContextProvider>
            <ErrorBoundary>
                <AuthClientProvider session={session}>
                    <QueryClientProvider client={queryClient}>
                        <ThemeProvider>
                            <DesignSystemProvider>
                                {Component.unauth ? (
                                    <Component {...pageProps} />
                                ) : (
                                    <ExchangeRatesBootstrap>
                                        <ToastProvider>
                                            <AbilityProvider
                                                ability={createUserAbility(session?.user, abilityRules)}
                                            >
                                                <FlagsProvider flags={flags}>
                                                    <NavProvider>
                                                        <MixPanelProvider>
                                                            <Hydrate state={pageProps.dehydratedState}>
                                                                <AppTemplate>
                                                                    <Component {...pageProps} />
                                                                </AppTemplate>
                                                            </Hydrate>
                                                            <NextNProgress
                                                                color={theme.colors.black.toString()}
                                                                startPosition={0.3}
                                                                stopDelayMs={200}
                                                                height={3}
                                                            />
                                                        </MixPanelProvider>
                                                    </NavProvider>
                                                </FlagsProvider>
                                            </AbilityProvider>
                                        </ToastProvider>
                                    </ExchangeRatesBootstrap>
                                )}
                            </DesignSystemProvider>
                        </ThemeProvider>
                    </QueryClientProvider>
                </AuthClientProvider>
                {['production', 'staging', 'development'].includes(appEnv) && (
                    <NPS.Script
                        globalId={session?.user?.globalId ?? null}
                        preferredLanguage={locale}
                    />
                )}
            </ErrorBoundary>
        </TolgeeContextProvider>
    );
}

const ExchangeRatesBootstrap = ({ children }: { children: ReactNode }) => {
    useExchangeRatesBootstrap();
    return <>{children}</>;
};
